import React, { useState } from "react";
import { Intent } from "@blueprintjs/core";
import { ButtonSave } from "components/buttons";
import { DataDisplayRecord } from "components/data-display";
import { DateToLocal } from "components/dates/DateToLocal";
import { GridLayout } from "components/grid";
import { ShowHide } from "components/layout";
import { NotificationToaster } from "components/notifications";
import { StatusBadge } from "components/status";
import {
    GreyFleetCheckInsuranceOverview,
    hasRequiredCoverTypes,
} from "pages/licence-checking/grey-fleet/check/overview";
import { BlobStorageService, LicenceCheckingService } from "services";
import PropTypes from "prop-types";

const defaultArray = [];

export function GreyFleetCheckInsurance({
    isLoading,
    checkData,
    evaluateStatus,
    reloadCheck,
    coverTypes = defaultArray,
}) {
    const badRequestCode = 400;
    const declaredCoverTypeIds = checkData?.insurance?.coverTypeIds ?? defaultArray;

    const [downloadingDocument, setDownloadingDocument] = useState(false);

    function onDocumentLinkClick() {
        setDownloadingDocument(true);
        LicenceCheckingService.getInsuranceDocument(checkData.id)
            .then(
                (response) => {
                    NotificationToaster.show(
                        Intent.SUCCESS,
                        "Downloading has successfully started."
                    );
                    BlobStorageService.downloadFile(
                        response.storageName,
                        response.containerName,
                        response.fileName,
                        response.token,
                        response.downloadName
                    ).catch(() => {
                        NotificationToaster.show(
                            Intent.DANGER,
                            "Failed to download file",
                            false
                        );
                    });
                },
                (error) => {
                    if (error.response.status === badRequestCode) {
                        NotificationToaster.show(
                            Intent.WARNING,
                            error.response.data
                        );
                    } else {
                        NotificationToaster.show(
                            Intent.DANGER,
                            "An unexpected error occurred while processing request."
                        );
                    }
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "An unexpected error occurred while processing request."
                );
            })
            .finally(() => {
                setDownloadingDocument(false);
            });
    }

    const getStatusBadgeProps = (evaluationField, textField) => {
        if (evaluationField) {
            return {
                iconName: "tick-circle",
                intent: Intent.SUCCESS,
                text: textField,
            };
        } else {
            return {
                iconName: "ban-circle",
                intent: Intent.DANGER,
                text: textField,
            };
        }
    };

    // Declared ids with no entry in the lookup are not shown, so a record whose every id is
    // unknown falls back to the business use badge.
    const declaredCoverTypes = declaredCoverTypeIds
        .map((coverTypeId) => coverTypes.find((coverType) => coverType.id === coverTypeId))
        .filter((coverType) => coverType != null);

    let coverRecord;

    if (declaredCoverTypes.length > 0) {
        coverRecord = (
            <DataDisplayRecord
                header="Type of Cover"
                data={declaredCoverTypes.map((coverType) => (
                    <div key={`cover-type-${coverType.id}`}>
                        {coverType.name}
                    </div>
                ))}
                loading={isLoading}
            />
        );
    } else {
        coverRecord = (
            <DataDisplayRecord
                header="Business Use"
                data={
                    <StatusBadge
                        {...getStatusBadgeProps(
                            checkData?.insurance?.businessUse,
                            checkData?.insurance?.businessUse
                                ? "Yes"
                                : "No"
                        )}
                    />
                }
                loading={isLoading}
            />
        );
    }

    // The overview already shows the declared cover beside the required cover once the company has
    // recorded a requirement that resolves, so this slot would repeat it. When nothing resolves the
    // overview shows neither record, so the slot stays as the only place the cover appears.
    const coverRecordDuplicatesOverview =
        hasRequiredCoverTypes(checkData?.insurance, coverTypes) &&
        declaredCoverTypes.length > 0;

    return (
        <div>
            <GreyFleetCheckInsuranceOverview
                isLoading={isLoading}
                checkData={checkData}
                evaluateStatus={evaluateStatus}
                reloadCheck={reloadCheck}
                coverTypes={coverTypes}
            />

            <ShowHide
                evaluator={checkData.insurance.result !== null}
                show={
                    <div className="spacer-bottom">
                        <h2>Insurance Details</h2>
                        <div className="spacer-bottom">
                            <GridLayout numberOfColumns={4} gap={30}>
                                <DataDisplayRecord
                                    header="Driver Named on Policy"
                                    data={
                                        <StatusBadge
                                            {...getStatusBadgeProps(
                                                checkData?.insurance
                                                    ?.nameCorrect,
                                                checkData?.employeeName
                                            )}
                                        />
                                    }
                                    loading={isLoading}
                                />
                                <DataDisplayRecord
                                    header="Vehicle Named on Policy"
                                    data={
                                        <StatusBadge
                                            {...getStatusBadgeProps(
                                                checkData?.insurance
                                                    ?.registrationCorrect,
                                                checkData?.registration
                                            )}
                                        />
                                    }
                                    loading={isLoading}
                                />
                                <DataDisplayRecord
                                    header="Policy Dates"
                                    data={
                                        <StatusBadge
                                            {...getStatusBadgeProps(
                                                checkData?.insurance
                                                    ?.policyDatesCorrect,
                                                <>
                                                    <DateToLocal format="DD/MM/YYYY">
                                                        {checkData?.insurance
                                                            ?.startDate ?? ""}
                                                    </DateToLocal>
                                                    {" - "}
                                                    <DateToLocal format="DD/MM/YYYY">
                                                        {checkData?.insurance
                                                            ?.endDate ?? ""}
                                                    </DateToLocal>
                                                </>
                                            )}
                                        />
                                    }
                                    loading={isLoading}
                                />
                                <ShowHide
                                    evaluator={!coverRecordDuplicatesOverview}
                                    show={coverRecord}
                                />
                            </GridLayout>
                        </div>

                        <DataDisplayRecord
                            header="Documents"
                            data={
                                <ShowHide
                                    evaluator={
                                        !!checkData?.insurance?.documentFile
                                    }
                                    show={
                                        <div className="inline-items">
                                            <ButtonSave
                                                id="download-insurance-document-button"
                                                intent={Intent.PRIMARY}
                                                onClick={onDocumentLinkClick}
                                                disabled={downloadingDocument}
                                                text="Download Document"
                                                icon={"document"}
                                            />
                                        </div>
                                    }
                                    hide={<span>No documents uploaded</span>}
                                />
                            }
                            loading={isLoading}
                        />
                    </div>
                }
            />
        </div>
    );
}

GreyFleetCheckInsurance.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    checkData: PropTypes.object.isRequired,
    evaluateStatus: PropTypes.func.isRequired,
    reloadCheck: PropTypes.func.isRequired,
    coverTypes: PropTypes.array,
};
